import { GetApp } from "@mui/icons-material";
import { Box, Chip, Typography } from "@mui/material";
import { MessageAttachment } from "models";
import React from "react";

interface FormattedMessageContentProps {
    content: any; // Draft.js RawDraftContentState or legacy string
    variant?: "body1" | "body2" | "caption";
    maxLines?: number;
    attachments?: MessageAttachment[];
    onDownloadAttachment?: (attachment: MessageAttachment) => void;
}

export const FormattedMessageContent: React.FC<FormattedMessageContentProps> = ({
    content,
    variant = "body2",
    maxLines,
    attachments,
    onDownloadAttachment
}) => {
    const renderContent = () => {
        try {
            // Se il contenuto è una stringa (messaggio legacy), mostralo direttamente
            if (typeof content === 'string') {
                return [{ text: content, type: 'unstyled', inlineStyleRanges: [] }];
            }

            // Se il contenuto è un oggetto Draft.js, processalo
            if (content && typeof content === 'object' && content.blocks && Array.isArray(content.blocks)) {
                // Valida che ogni blocco abbia la struttura corretta
                const validBlocks = content.blocks.filter((block: any) =>
                    block && typeof block === 'object' && typeof block.text === 'string'
                );
                return validBlocks.length > 0 ? validBlocks : [{ text: '', type: 'unstyled', inlineStyleRanges: [] }];
            }

            // Fallback per contenuto non riconosciuto
            return [{ text: '', type: 'unstyled', inlineStyleRanges: [] }];
        } catch (error) {
            console.warn('Errore nel processare il contenuto del messaggio:', error);
            return [{ text: 'Errore nel caricamento del messaggio', type: 'unstyled', inlineStyleRanges: [] }];
        }
    };

    const blocks = renderContent();

    const renderBlock = (block: any, index: number) => {
        // Validazione del blocco
        if (!block || typeof block !== 'object') {
            return <span key={index}>Contenuto non valido</span>;
        }

        const { text = '', type = 'unstyled', inlineStyleRanges = [] } = block;

        // Assicurati che text sia una stringa
        const safeText = typeof text === 'string' ? text : '';

        // Applica gli stili inline
        const textParts: Array<{ text: string; styles: string[] }> = [];

        if (Array.isArray(inlineStyleRanges) && inlineStyleRanges.length > 0 && safeText.length > 0) {
            try {
                // Crea una mappa dei caratteri con i loro stili
                const charStyles: string[][] = new Array(safeText.length).fill(null).map(() => []);

                inlineStyleRanges.forEach((range: any) => {
                    if (range && typeof range.offset === 'number' && typeof range.length === 'number' && typeof range.style === 'string') {
                        const endOffset = Math.min(range.offset + range.length, safeText.length);
                        for (let i = range.offset; i < endOffset; i++) {
                            if (charStyles[i]) {
                                charStyles[i].push(range.style);
                            }
                        }
                    }
                });

                // Raggruppa i caratteri con gli stessi stili
                let currentText = '';
                let currentStyles: string[] = [];

                for (let i = 0; i < safeText.length; i++) {
                    const styles = charStyles[i] || [];
                    const stylesStr = styles.sort().join(',');
                    const currentStylesStr = currentStyles.sort().join(',');

                    if (stylesStr === currentStylesStr) {
                        currentText += safeText[i];
                    } else {
                        if (currentText) {
                            textParts.push({ text: currentText, styles: currentStyles });
                        }
                        currentText = safeText[i];
                        currentStyles = styles;
                    }
                }

                if (currentText) {
                    textParts.push({ text: currentText, styles: currentStyles });
                }
            } catch (error) {
                console.warn('Errore nel processare gli stili inline:', error);
                textParts.push({ text: safeText, styles: [] });
            }
        } else {
            textParts.push({ text: safeText, styles: [] });
        }

        const renderTextPart = (part: { text: string; styles: string[] }, partIndex: number) => {
            let element = <span key={partIndex}>{part.text}</span>;

            if (part.styles.includes('BOLD')) {
                element = <strong key={partIndex}>{part.text}</strong>;
            }
            if (part.styles.includes('ITALIC')) {
                element = <em key={partIndex}>{element}</em>;
            }

            return element;
        };

        const content = textParts.map(renderTextPart);

        // Renderizza in base al tipo di blocco
        switch (type) {
            case 'unordered-list-item':
                return <li key={index}>{content}</li>;
            case 'ordered-list-item':
                return <li key={index}>{content}</li>;
            default:
                return <span key={index}>{content}</span>;
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Box>
            <Typography
                variant={variant}
                component="div"
                sx={{
                    ...(maxLines && {
                        display: '-webkit-box',
                        WebkitLineClamp: maxLines,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }),
                    '& ul, & ol': {
                        margin: '4px 0',
                        paddingLeft: '16px',
                    },
                    '& li': {
                        margin: '2px 0',
                    },
                    whiteSpace: 'pre-wrap',
                }}
            >
                {blocks.map(renderBlock)}
            </Typography>

            {/* Visualizzazione allegati */}
            {attachments && attachments.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    {attachments.map((attachment) => (
                        <Chip
                            key={attachment.id}
                            label={`${attachment.fileName} (${formatFileSize(attachment.fileSize)})`}
                            size="small"
                            sx={{ mr: 1, mb: 0.5 }}
                            onClick={() => onDownloadAttachment && onDownloadAttachment(attachment)}
                            icon={<GetApp />}
                            clickable
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};